import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Container, Box, Typography, Grid, Button } from "@material-ui/core";
import { PaymentForm } from "./components/PaymentForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useConfig } from "../../components/config/ConfigContext";
import { FEATURE_NAME } from "../checkoutPage/components/TabPayDeposit/paymentConstants";
import { RouteComponentProps } from "react-router";
import PaymentSummary from "./components/PaymentSummary";
import useStyles from "./components/PaymentForm.styles";
import { useEffect, useState } from "react";
import { fetchBalance, fetchQuote } from "../../store/QuoteSlice";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { PriceFormat } from "../../components/typography/PriceFormat";
import { useHistory } from "react-router-dom";
import { RootState } from "../../app/rootReducer";

const queryClient = new QueryClient();
type Props = RouteComponentProps<{ id: string }>;
export const PaymentPage = ({ match }: Props) => {
  const quoteId = match.params.id;
  const { getFeatureConfig } = useConfig();
  const stripeKey = getFeatureConfig(FEATURE_NAME)?.StripeKey;
  const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { quote } = useSelector((state: RootState) => state.quote);

  const [paymentSuccessful, setPaymentSuccessful] = useState<boolean>(false);
  const [amountPaid, setAmountPaid] = useState<number>(0);

  useEffect(() => {
    dispatch(fetchQuote(quoteId));
    dispatch(fetchBalance(quoteId));
  }, [quoteId, dispatch]);

  useEffect(() => {
    if (paymentSuccessful) {
      dispatch(fetchQuote(quoteId, true));
    }
  }, [paymentSuccessful, quoteId, dispatch]);

  const handleReturnToQuote = () => {
    history.push(`/quote/${quoteId}`);
  };

  if (!stripeKey || !stripePromise) {
    return (
      <Container maxWidth="sm">
        <Box mt={4} textAlign="center">
          <Typography variant="h5" color="error" gutterBottom>
            Payments cannot be processed at the moment.
          </Typography>
          <Typography variant="body1">Please try again later or contact support.</Typography>
        </Box>
      </Container>
    );
  }

  if (paymentSuccessful) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          textAlign="center"
        >
          <CheckCircleIcon style={{ fontSize: 80, color: "green" }} />
          <Typography variant="h1" gutterBottom>
            You're all set!
          </Typography>
          <Typography variant="body1" paragraph>
            We've received your payment of <PriceFormat value={amountPaid} /> for Quote {quote?.name}.
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            It may take up to 5 minutes for your balance to update.
          </Typography>
          <Button variant="contained" color="secondary" onClick={handleReturnToQuote}>
            Back to Quote
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Box bgcolor={"#ffffff"}>
        <Container maxWidth="lg">
          <Box m={4} className={classes.root}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={8}>
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    onPaymentSuccess={(amount) => {
                      setPaymentSuccessful(true);
                      setAmountPaid(amount);
                    }}
                  />
                </Elements>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <PaymentSummary quoteId={quoteId} />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </QueryClientProvider>
  );
};
